/**
 * @module Auth0TenantRegistry
 *
 * Tenant Class Registry - Single source of truth for all tenant class definitions.
 *
 * ADDING A NEW TENANT CLASS:
 * 1. Add an entry to TENANT_CLASS_DEFINITIONS with all required fields
 * 2. That's it! No changes needed to auth0-tenant-utils.mjs or auth0-domain-utils.mjs
 *
 * Each tenant class defines:
 * - tenantClass: The base tenant class identifier (e.g., 'shipstation')
 * - tenantNames: Mapping of environment -> tenant name (derived automatically if not specified)
 * - deriveAuth0Domain: Function to derive Auth0 tenant domain from tenant name
 * - deriveCustomDomain: Function to derive custom signin domain from environment
 * - customDomainPrefix: Prefix used in custom domain (e.g., 'signin', 'partners-signin')
 */

/* eslint-disable import/extensions */

// =============================================================================
// Environment Definitions
// =============================================================================

/**
 * All supported environments across tenant classes.
 * @type {readonly string[]}
 */
const ENVIRONMENTS = Object.freeze([
  'prod',
  'stage',
  'dev',
  'local',
  'local-z',
  'local-z2',
  'local-z3',
]);

/**
 * Environments considered "dev" (non-production).
 * @type {readonly string[]}
 */
const DEV_ENVIRONMENTS = Object.freeze([
  'dev',
  'local',
  'local-z',
  'local-z2',
  'local-z3',
]);

/**
 * Environments considered "local" (running on developer machine).
 * @type {readonly string[]}
 */
const LOCAL_ENVIRONMENTS = Object.freeze([
  'local',
  'local-z',
  'local-z2',
  'local-z3',
]);

// =============================================================================
// Helper to build tenant names mapping
// =============================================================================

/**
 * Build the tenant names mapping for a tenant class.
 * Convention: prod = tenantClass, others = tenantClass-environment
 * @param {string} tenantClass
 * @returns {Object<string, string>}
 */
const buildTenantNamesMapping = (tenantClass) => {
  const mapping = ENVIRONMENTS.reduce((acc, env) => {
    acc[env] = env === 'prod' ? tenantClass : `${tenantClass}-${env}`;
    return acc;
  }, {});
  return Object.freeze(mapping);
};

// =============================================================================
// Tenant Class Definitions
// =============================================================================

/**
 * @typedef {Object} TenantClassDefinition
 * @property {string} tenantClass - The tenant class identifier (e.g., 'shipstation')
 * @property {string} description - Human-readable description
 * @property {Object<string, string>} tenantNames - Mapping of environment -> tenant name
 * @property {(tenantName: string, env: string) => string} deriveAuth0Domain - Derive Auth0 tenant domain
 * @property {(env: string) => string} deriveCustomDomain - Derive custom signin domain
 * @property {string} customDomainPrefix - Prefix used in custom domain URLs
 * @property {string[]} activeIdps - IdP identifiers active for this tenant class
 * @property {string[]} alwaysAvailableIdps - IdP identifiers always shown on login for this tenant class
 */

/**
 * Registry of all tenant class definitions.
 *
 * TO ADD A NEW TENANT CLASS: Add a new entry here with all required fields.
 * No other files need to be modified.
 *
 * @type {readonly TenantClassDefinition[]}
 */
const TENANT_CLASS_DEFINITIONS = Object.freeze([
  Object.freeze({
    tenantClass: 'shipstation',
    description: 'ShipStation main tenant for consumer applications',
    tenantNames: buildTenantNamesMapping('shipstation'),

    /**
     * Derive Auth0 tenant domain (e.g., shipstation-local-z2.us.auth0.com)
     * ShipStation uses .us.auth0.com for local-z* environments, .auth0.com for others
     */
    deriveAuth0Domain: (tenantName, env) => {
      if (env.startsWith('local-z')) {
        return `${tenantName}.us.auth0.com`;
      }
      return `${tenantName}.auth0.com`;
    },

    /**
     * Derive custom signin domain (e.g., signin-local-z2.shipstation.com)
     * ShipStation dev uses sslocal.com, all others use shipstation.com
     */
    deriveCustomDomain: (env) => {
      const baseDomain = env === 'dev' ? 'sslocal.com' : 'shipstation.com';
      if (env === 'prod') {
        return `signin.${baseDomain}`;
      }
      return `signin-${env}.${baseDomain}`;
    },

    customDomainPrefix: 'signin',

    // Identity providers active for this tenant class
    activeIdps: ['shopify', 'shopify-oauth2', 'google-oauth2', 'woot-oauth2', 'test-idp', 'test-idp-custom'],
    // Identity providers always shown on login screen (step 1 of progressive auth)
    alwaysAvailableIdps: ['google-oauth2'],
  }),

  Object.freeze({
    tenantClass: 'shipstation-partners',
    description: 'ShipStation partners tenant for partner applications',
    tenantNames: buildTenantNamesMapping('shipstation-partners'),

    /**
     * Derive Auth0 tenant domain (e.g., shipstation-partners-local-z2.us.auth0.com)
     * Partners always uses .us.auth0.com for all environments
     */
    // eslint-disable-next-line no-unused-vars -- env param reserved for future per-environment logic
    deriveAuth0Domain: (tenantName, _env) => `${tenantName}.us.auth0.com`,

    /**
     * Derive custom signin domain (e.g., partners-signin-local-z2.shipstation.com)
     * Partners always uses shipstation.com (no sslocal.com variant)
     */
    deriveCustomDomain: (env) => {
      if (env === 'prod') {
        return 'partners-signin.shipstation.com';
      }
      return `partners-signin-${env}.shipstation.com`;
    },

    customDomainPrefix: 'partners-signin',

    // Identity providers active for this tenant class
    activeIdps: ['google-oauth2', 'test-idp', 'test-idp-custom'],
    // Identity providers always shown on login screen (step 1 of progressive auth)
    alwaysAvailableIdps: ['google-oauth2'],
  }),
]);

// =============================================================================
// Build lookup indexes for fast access
// =============================================================================

// Index by tenantClass for fast lookup
const definitionsByTenantClass = new Map(TENANT_CLASS_DEFINITIONS.map(def => [def.tenantClass, def]));

// Reverse index: tenant name -> { tenantClass, environment }
const tenantNameIndex = TENANT_CLASS_DEFINITIONS.reduce((index, def) => {
  Object.entries(def.tenantNames).forEach(([env, tenantName]) => {
    index.set(tenantName, { tenantClass: def.tenantClass, environment: env });
  });
  return index;
}, new Map());

// =============================================================================
// Registry Lookup Functions
// =============================================================================

/**
 * Get the tenant class definition for a given tenant class.
 * @param {string} tenantClass - The tenant class name (e.g., 'shipstation')
 * @returns {TenantClassDefinition | null}
 */
const getTenantClassDefinition = (tenantClass) => {
  return definitionsByTenantClass.get(tenantClass) ?? null;
};

/**
 * Get all registered tenant class definitions.
 * @returns {TenantClassDefinition[]}
 */
const getAllTenantClassDefinitions = () => {
  return [...TENANT_CLASS_DEFINITIONS];
};

/**
 * Get all registered tenant class names.
 * @returns {string[]}
 */
const getAllTenantClasses = () => {
  return TENANT_CLASS_DEFINITIONS.map(def => def.tenantClass);
};

/**
 * Parse a tenant name into its tenant class and environment.
 * Uses the pre-built index for O(1) lookup.
 * @param {string} tenantName - The tenant name (e.g., 'shipstation-local-z2')
 * @returns {Object<string, string>|null} Object with tenantClass and environment properties, or null
 */
const parseTenantName = (tenantName) => {
  if (!tenantName) return null;
  return tenantNameIndex.get(tenantName) ?? null;
};

/**
 * Derive the tenant name from tenant class and environment.
 * @param {string} tenantClass - The tenant class (e.g., 'shipstation')
 * @param {string} environment - The environment (e.g., 'local-z2', 'prod')
 * @returns {string | null} The tenant name or null if invalid
 */
const deriveTenantName = (tenantClass, environment) => {
  const def = getTenantClassDefinition(tenantClass);
  if (!def) return null;
  return def.tenantNames[environment] ?? null;
};

/**
 * Check if a tenant name belongs to a specific tenant class.
 * @param {string} tenantName - The tenant name (e.g., 'shipstation-local-z2')
 * @param {string} tenantClass - The tenant class (e.g., 'shipstation')
 * @returns {boolean}
 */
const isTenantInClass = (tenantName, tenantClass) => {
  const parsed = parseTenantName(tenantName);
  return parsed?.tenantClass === tenantClass;
};

/**
 * Check if a tenant name is for a specific environment.
 * @param {string} tenantName - The tenant name (e.g., 'shipstation-local-z2')
 * @param {string} environment - The environment (e.g., 'local-z2')
 * @returns {boolean}
 */
const isTenantForEnvironment = (tenantName, environment) => {
  const parsed = parseTenantName(tenantName);
  return parsed?.environment === environment;
};

/**
 * Check if a tenant is valid (recognized by the registry).
 * @param {string} tenantName - The tenant name to validate
 * @returns {boolean}
 */
const isValidTenant = (tenantName) => {
  return tenantNameIndex.has(tenantName);
};

/**
 * Get the tenant name for a specific tenant class and environment.
 * @param {string} tenantClass - The tenant class
 * @param {string} environment - The environment
 * @returns {string | null}
 */
const getTenantName = (tenantClass, environment) => {
  const def = getTenantClassDefinition(tenantClass);
  return def?.tenantNames[environment] ?? null;
};

// =============================================================================
// Domain Utilities
// =============================================================================

/**
 * Derive the Auth0 domain for a tenant.
 * @param {string} tenantName - The tenant name (e.g., 'shipstation-local-z2')
 * @returns {string | null} The Auth0 domain or null if tenant not recognized
 */
const deriveAuth0DomainForTenant = (tenantName) => {
  const parsed = parseTenantName(tenantName);
  if (!parsed) return null;

  const def = getTenantClassDefinition(parsed.tenantClass);
  if (!def) return null;

  return def.deriveAuth0Domain(tenantName, parsed.environment);
};

/**
 * Derive the custom signin domain for a tenant.
 * @param {string} tenantName - The tenant name (e.g., 'shipstation-local-z2')
 * @returns {string | null} The custom domain or null if tenant not recognized
 */
const deriveCustomDomainForTenant = (tenantName) => {
  const parsed = parseTenantName(tenantName);
  if (!parsed) return null;

  const def = getTenantClassDefinition(parsed.tenantClass);
  if (!def) return null;

  return def.deriveCustomDomain(parsed.environment);
};

/**
 * Resolve tenant name from a custom domain.
 * @param {string} customDomain - The custom domain (e.g., 'signin-local-z2.shipstation.com')
 * @returns {string | null} The tenant name or null if not recognized
 */
const resolveTenantFromCustomDomain = (customDomain) => {
  if (!customDomain) return null;

  const subdomain = customDomain.split('.')[0];

  // Find matching tenant class by custom domain prefix
  const matchingDef = TENANT_CLASS_DEFINITIONS.find((def) => {
    const prefix = def.customDomainPrefix;
    // Check for exact match (prod) or prefix match (non-prod)
    if (subdomain === prefix) return true;
    if (subdomain.startsWith(`${prefix}-`)) {
      const env = subdomain.slice(prefix.length + 1);
      return Boolean(def.tenantNames[env]);
    }
    return false;
  });

  if (!matchingDef) return null;

  const prefix = matchingDef.customDomainPrefix;
  if (subdomain === prefix) {
    return matchingDef.tenantNames.prod;
  }

  const env = subdomain.slice(prefix.length + 1);
  return matchingDef.tenantNames[env] ?? null;
};

/**
 * Get the tenant class from a custom domain.
 * @param {string} customDomain - The custom domain (e.g., 'partners-signin-local-z2.shipstation.com')
 * @returns {string | null} The tenant class or null if not recognized
 */
const getTenantClassFromCustomDomain = (customDomain) => {
  const tenantName = resolveTenantFromCustomDomain(customDomain);
  if (!tenantName) return null;

  const parsed = parseTenantName(tenantName);
  return parsed?.tenantClass ?? null;
};

// =============================================================================
// IdP Utilities
// =============================================================================

/**
 * Get all tenant classes that have a specific IdP active.
 * Used by auth-idp-config.mjs to populate the 'active' array for each IdP.
 * @param {string} idpId - The IdP identifier (e.g., 'google-oauth2', 'shopify')
 * @returns {string[]} Array of tenant class names that have this IdP active
 */
const getActiveTenantClassesForIdp = (idpId) => {
  return TENANT_CLASS_DEFINITIONS
    .filter(def => def.activeIdps?.includes(idpId))
    .map(def => def.tenantClass);
};

/**
 * Get all tenant classes that have a specific IdP always available.
 * Used by auth-idp-config.mjs to populate the 'alwaysAvailable' array for each IdP.
 * @param {string} idpId - The IdP identifier (e.g., 'google-oauth2')
 * @returns {string[]} Array of tenant class names that have this IdP always available
 */
const getAlwaysAvailableTenantClassesForIdp = (idpId) => {
  return TENANT_CLASS_DEFINITIONS
    .filter(def => def.alwaysAvailableIdps?.includes(idpId))
    .map(def => def.tenantClass);
};

// =============================================================================
// Custom Domain Utilities
// =============================================================================

/**
 * Get all production custom domain prefixes.
 * These are the subdomain prefixes that indicate a production environment
 * (e.g., 'signin' for signin.shipstation.com, 'partners-signin' for partners-signin.shipstation.com).
 * @returns {string[]} Array of production custom domain prefixes
 */
const getProdCustomDomainPrefixes = () => {
  return TENANT_CLASS_DEFINITIONS.map(def => def.customDomainPrefix);
};

// =============================================================================
// Exports
// =============================================================================

export {
  // Constants
  ENVIRONMENTS,
  DEV_ENVIRONMENTS,
  LOCAL_ENVIRONMENTS,
  TENANT_CLASS_DEFINITIONS,

  // Lookup functions
  getTenantClassDefinition,
  getAllTenantClassDefinitions,
  getAllTenantClasses,

  // Tenant name utilities
  deriveTenantName,
  parseTenantName,
  isTenantInClass,
  isTenantForEnvironment,
  isValidTenant,
  getTenantName,

  // Domain utilities
  deriveAuth0DomainForTenant,
  deriveCustomDomainForTenant,
  resolveTenantFromCustomDomain,
  getTenantClassFromCustomDomain,

  // IdP utilities
  getActiveTenantClassesForIdp,
  getAlwaysAvailableTenantClassesForIdp,

  // Custom domain utilities
  getProdCustomDomainPrefixes,
};
